/**
 * HelpModal Komponente
 * Benutzerhandbuch und Hilfe für CherryPulse
 */

import { useState, memo } from 'react';
import { Modal } from './Modal';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type HelpSection = 'overview' | 'agencies' | 'ratings' | 'voice' | 'scores' | 'dashboard' | 'faq';

const sections: { id: HelpSection; icon: string; title: string }[] = [
  { id: 'overview', icon: '📖', title: 'Überblick' },
  { id: 'agencies', icon: '🏢', title: 'Agenturen' },
  { id: 'ratings', icon: '⭐', title: 'Bewertungen' },
  { id: 'voice', icon: '🎤', title: 'Sprachassistent' },
  { id: 'scores', icon: '📊', title: 'Notensystem' },
  { id: 'dashboard', icon: '📈', title: 'Dashboard' },
  { id: 'faq', icon: '❓', title: 'FAQ' },
];

export const HelpModal = memo(function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const [activeSection, setActiveSection] = useState<HelpSection>('overview');

  const renderContent = (): JSX.Element => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Willkommen bei CherryPulse</h3>
            <p className="text-charcoal-600">
              CherryPulse ist Ihr Tool zur strukturierten Bewertung von Agenturen.
              Erfassen Sie Bewertungen zu Qualitätskriterien und behalten Sie
              die Entwicklung Ihrer Agenturpartner im Blick.
            </p>

            <div className="bg-cherry-50 rounded-xl p-4 border border-cherry-200">
              <h4 className="font-semibold text-cherry-800 mb-2">Schnellstart</h4>
              <ol className="list-decimal list-inside space-y-2 text-charcoal-700 text-sm">
                <li>Fügen Sie eine Agentur über "Neue Agentur" hinzu</li>
                <li>Starten Sie eine Bewertung mit dem Bewerten-Button</li>
                <li>Wählen Sie manuell oder per Sprachassistent</li>
                <li>Vergeben Sie Sterne (0-5) für jedes Kriterium</li>
                <li>Analysieren Sie Trends im Dashboard</li>
              </ol>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-charcoal-50 rounded-lg p-3">
                <div className="text-2xl mb-1">🏢</div>
                <div className="text-sm font-medium text-charcoal-800">Agenturen verwalten</div>
                <div className="text-xs text-charcoal-500">Hinzufügen, bearbeiten, löschen</div>
              </div>
              <div className="bg-charcoal-50 rounded-lg p-3">
                <div className="text-2xl mb-1">⭐</div>
                <div className="text-sm font-medium text-charcoal-800">Bewertungen abgeben</div>
                <div className="text-xs text-charcoal-500">Manuell oder per Sprache</div>
              </div>
              <div className="bg-charcoal-50 rounded-lg p-3">
                <div className="text-2xl mb-1">📊</div>
                <div className="text-sm font-medium text-charcoal-800">Trends analysieren</div>
                <div className="text-xs text-charcoal-500">Scores, Verläufe</div>
              </div>
              <div className="bg-charcoal-50 rounded-lg p-3">
                <div className="text-2xl mb-1">📄</div>
                <div className="text-sm font-medium text-charcoal-800">PDF exportieren</div>
                <div className="text-xs text-charcoal-500">Scorecards teilen</div>
              </div>
            </div>
          </div>
        );

      case 'agencies':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Agenturen verwalten</h3>

            <div className="space-y-3">
              <div className="border-l-4 border-cherry-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Neue Agentur anlegen</h4>
                <p className="text-sm text-charcoal-600">
                  Klicken Sie auf "Neue Agentur" auf der Startseite oder in der Agenturliste.
                  Geben Sie Name, Typ (z.B. Full-Service, Digital, PR) und optional ein Logo ein.
                </p>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Agentur bearbeiten</h4>
                <p className="text-sm text-charcoal-600">
                  Öffnen Sie die Detailseite einer Agentur und klicken Sie auf das Bearbeiten-Symbol
                  neben dem Namen. Hier können Sie alle Angaben anpassen.
                </p>
              </div>

              <div className="border-l-4 border-amber-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Agenturtypen</h4>
                <p className="text-sm text-charcoal-600">
                  Verfügbare Typen: Full-Service, Digital, PR, Event, Media, Design, Content, Social Media,
                  Performance, Branding, Sonstige. Der Typ hilft bei der Kategorisierung und Filterung.
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Agentur löschen</h4>
                <p className="text-sm text-charcoal-600">
                  Auf der Detailseite finden Sie den Löschen-Button.
                  <strong className="text-red-600"> Achtung: Alle Bewertungen werden unwiderruflich gelöscht!</strong>
                </p>
              </div>
            </div>
          </div>
        );

      case 'ratings':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Bewertungen abgeben</h3>

            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-2">Zwei Bewertungsmodi</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg mb-1">📝</div>
                  <div className="text-sm font-medium">Manuell</div>
                  <div className="text-xs text-charcoal-500">Klicken Sie durch alle Kriterien</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg mb-1">🎤</div>
                  <div className="text-sm font-medium">Sprachgesteuert</div>
                  <div className="text-xs text-charcoal-500">Sprechen Sie Ihre Bewertung</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-charcoal-800">Bewertungsablauf</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-charcoal-600">
                <li><strong>Anlass wählen:</strong> Jahresreview, Projektabschluss, Quartalsbewertung, etc.</li>
                <li><strong>Kriterien bewerten:</strong> Von Strategiekompetenz bis Chemie</li>
                <li><strong>Optional: Notizen</strong> zu einzelnen Kriterien hinzufügen</li>
                <li><strong>Speichern:</strong> Die Bewertung wird der Historie hinzugefügt</li>
              </ol>
            </div>

            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2">Bewertungsnummerierung</h4>
              <p className="text-sm text-charcoal-700">
                Bewertungen werden chronologisch nummeriert: <strong>Bewertung 1</strong> ist die älteste,
                die höchste Nummer ist die aktuellste Bewertung.
              </p>
            </div>
          </div>
        );

      case 'voice':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Sprachassistent</h3>

            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">So funktioniert's</h4>
              <p className="text-sm text-charcoal-700 mb-3">
                Der KI-Sprachassistent führt Sie durch die Bewertung. Sprechen Sie natürlich -
                der Assistent versteht Schulnoten, Beschreibungen und Feedback.
              </p>
              <div className="text-sm text-charcoal-600">
                <strong>Beispiele:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>"Die Strategiekompetenz ist eine 2+"</li>
                  <li>"Kreativität war diesmal nur befriedigend"</li>
                  <li>"Budget wurde gut eingehalten, Note 2"</li>
                </ul>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-charcoal-800">Tipps für die Sprachbewertung</h4>
              <ul className="space-y-2 text-sm text-charcoal-600">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span>
                  <span>Sprechen Sie in ruhiger Umgebung für beste Erkennung</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span>
                  <span>Sie können jederzeit Korrekturen vornehmen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span>
                  <span>Der Assistent fragt nach, wenn etwas unklar ist</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span>
                  <span>Wechseln Sie jederzeit zwischen Sprach- und Manuellmodus</span>
                </li>
              </ul>
            </div>

            <div className="bg-charcoal-50 rounded-xl p-4">
              <h4 className="font-semibold text-charcoal-800 mb-2">Score-Korrektur</h4>
              <p className="text-sm text-charcoal-600">
                Klicken Sie auf einen erfassten Score, um ihn manuell zu korrigieren.
                Ein Popup mit Notenauswahl erscheint.
              </p>
            </div>
          </div>
        );

      case 'scores':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Bewertungssystem</h3>

            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-3">CherryScore (0-5 Sterne)</h4>
              <p className="text-sm text-charcoal-700 mb-3">
                Die Bewertung erfolgt auf einer Skala von 0 bis 5:
              </p>
              <div className="space-y-1.5 text-sm">
                {[
                  { stars: 5, label: 'Sehr überzeugend', color: 'bg-green-100 text-green-800' },
                  { stars: 4, label: 'Überzeugend', color: 'bg-green-50 text-green-700' },
                  { stars: 3, label: 'Durchschnitt', color: 'bg-amber-100 text-amber-800' },
                  { stars: 2, label: 'Weniger überzeugend', color: 'bg-orange-100 text-orange-800' },
                  { stars: 1, label: 'Nicht überzeugend', color: 'bg-red-100 text-red-800' },
                  { stars: 0, label: 'Nicht bewertbar', color: 'bg-charcoal-100 text-charcoal-600' },
                ].map((item) => (
                  <div key={item.stars} className={`flex items-center justify-between rounded-lg px-3 py-1.5 ${item.color}`}>
                    <span className="font-medium">{item.stars} {'★'.repeat(item.stars)}{'☆'.repeat(5 - item.stars)}</span>
                    <span className="text-xs">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-charcoal-800">3 Bewertungsebenen</h4>
              <div className="space-y-2 text-sm">
                {[
                  { icon: '🏢', name: 'Agentur', desc: 'Eignung, Kompetenz, Marktverständnis' },
                  { icon: '👥', name: 'Team', desc: 'Strategie, Kreation, Beratung, Dynamik' },
                  { icon: '🤝', name: 'Chemie', desc: 'Passung, Begeisterung, Verlässlichkeit' },
                ].map((item) => (
                  <div key={item.name} className="flex items-center gap-3 bg-charcoal-50 rounded-lg p-3">
                    <span className="text-xl">{item.icon}</span>
                    <div>
                      <span className="font-medium text-charcoal-800">{item.name}</span>
                      <span className="text-charcoal-500 ml-2">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        );

      case 'dashboard':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Dashboard</h3>

            <p className="text-charcoal-600">
              Das Dashboard gibt Ihnen einen Überblick über alle Agenturen und deren Entwicklung.
            </p>

            <div className="space-y-3">
              <div className="border-l-4 border-cherry-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Kennzahlen-Kacheln</h4>
                <p className="text-sm text-charcoal-600">
                  Sehen Sie auf einen Blick: Anzahl Agenturen, Durchschnitts-Score
                  und Trend-Indikatoren im Vergleich zum Vormonat.
                </p>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Top & Bottom Performer</h4>
                <p className="text-sm text-charcoal-600">
                  Die besten und die verbesserungswürdigen Agenturen werden hervorgehoben,
                  damit Sie schnell Handlungsbedarf erkennen.
                </p>
              </div>

              <div className="border-l-4 border-emerald-500 pl-4">
                <h4 className="font-semibold text-charcoal-800">Zeitraumfilter</h4>
                <p className="text-sm text-charcoal-600">
                  Filtern Sie nach: Alle Zeit, Letztes Jahr, Letzte 6 Monate, Letzte 3 Monate, Letzter Monat.
                </p>
              </div>
            </div>

            <div className="bg-charcoal-50 rounded-xl p-4">
              <h4 className="font-semibold text-charcoal-800 mb-2">Trend-Visualisierung</h4>
              <p className="text-sm text-charcoal-600">
                Auf der Agentur-Detailseite finden Sie drei Verlaufsdiagramme:
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-charcoal-600 space-y-1">
                <li><strong>Gesamtbewertung:</strong> Liniendiagramm des CherryScore-Durchschnitts</li>
                <li><strong>Kriterienverlauf:</strong> Entwicklung einzelner Bewertungskriterien</li>
              </ul>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-charcoal-900">Häufige Fragen</h3>

            <div className="space-y-3">
              {[
                {
                  q: 'Kann ich eine Bewertung nachträglich ändern?',
                  a: 'Ja, klicken Sie auf das Bearbeiten-Symbol bei einer Bewertung auf der Agentur-Detailseite.',
                },
                {
                  q: 'Was passiert beim Löschen einer Agentur?',
                  a: 'Alle Bewertungen und Daten dieser Agentur werden unwiderruflich gelöscht.',
                },
                {
                  q: 'Wie funktioniert die Test-/Produktionsumgebung?',
                  a: 'Über das Toggle im Header können Sie zwischen Test- und Produktionsdaten wechseln. Testdaten sind isoliert.',
                },
                {
                  q: 'Kann ich Bewertungen exportieren?',
                  a: 'Ja, auf der Agentur-Detailseite können Sie eine Scorecard als PDF exportieren.',
                },
                {
                  q: 'Warum ist Note 1 "besser" als Note 6?',
                  a: 'CherryPulse verwendet das deutsche Schulnotensystem: 1 = sehr gut, 6 = ungenügend.',
                },
                {
                  q: 'Was bedeuten die Farben in den Diagrammen?',
                  a: 'Grün = gut (Note 1-2), Gelb = befriedigend (Note 3-4), Rot = mangelhaft (Note 5-6).',
                },
              ].map((faq, index) => (
                <details key={index} className="bg-charcoal-50 rounded-xl overflow-hidden">
                  <summary className="px-4 py-3 cursor-pointer font-medium text-charcoal-800 hover:bg-charcoal-100 transition-colors">
                    {faq.q}
                  </summary>
                  <div className="px-4 pb-3 text-sm text-charcoal-600">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>

            <div className="bg-cherry-50 rounded-xl p-4 border border-cherry-200 mt-6">
              <h4 className="font-semibold text-cherry-800 mb-2">Weitere Hilfe benötigt?</h4>
              <p className="text-sm text-charcoal-700">
                Bei technischen Problemen oder Fragen wenden Sie sich an Ihren Administrator.
              </p>
            </div>
          </div>
        );

      default:
        return <div>Inhalt nicht gefunden</div>;
    }
  };

  const icon = (
    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-cherry-100 rounded-xl flex items-center justify-center">
      <span className="text-lg sm:text-xl">📚</span>
    </div>
  );

  const footer = (
    <p className="text-xs text-charcoal-500 text-center">
      CherryPulse v0.3 • Agentur-Qualität messbar machen
    </p>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Hilfe & Handbuch"
      subtitle="CherryPulse Benutzerhandbuch"
      icon={icon}
      size="3xl"
      flexBody
      maxHeight="80vh"
      backdropClassName="bg-black/50 backdrop-blur-sm"
      footer={footer}
    >
      {/* Mobile Navigation Dropdown */}
      <div className="shrink-0 sm:hidden px-4 py-3 border-b border-charcoal-100 bg-charcoal-50/50">
        <select
          value={activeSection}
          onChange={(e) => setActiveSection(e.target.value as HelpSection)}
          className="w-full appearance-none px-4 py-2.5 bg-white border-2 border-charcoal-200 rounded-xl text-charcoal-900 font-medium focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:border-transparent"
          aria-label="Thema auswählen"
        >
          {sections.map((section) => (
            <option key={section.id} value={section.id}>
              {section.icon} {section.title}
            </option>
          ))}
        </select>
      </div>

      {/* Content Area */}
      <div className="flex min-h-0 flex-1 sm:h-[calc(80vh-8rem)]">
        {/* Sidebar Navigation - Desktop only */}
        <nav className="hidden sm:block w-48 shrink-0 border-r border-charcoal-100 bg-charcoal-50/50 overflow-y-auto">
          <div className="p-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-cherry-600 focus:ring-offset-2 ${
                  activeSection === section.id
                    ? 'bg-cherry-100 text-cherry-700'
                    : 'text-charcoal-600 hover:bg-charcoal-100'
                }`}
              >
                <span>{section.icon}</span>
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">
          {renderContent()}
        </div>
      </div>
    </Modal>
  );
});

export default HelpModal;
